import { Inject, Injectable } from '@angular/core';
import { DwDataPageResults, DwDataRequest, DwDatasource, DwItemType, DwMetaDataService, DwMetaDataServiceToken, DwOrmDataHierarchy, DwOrmDataHierarchyUtil, DwOrmDataService, DwOrmDataServiceToken, DwOrmDataSource, DwQuery, DwContextService, DwLookupInfo, DwSecurityUserService, sortArrayByField, DwQueryHelper } from '@devwareapps/devware-cap';
import { combineLatest, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { EmployeeReviewQuestionSetupEntity, PerformanceManagementMetaDataItemNames, PerformanceManagementMetaDataLookups, EthosUserEntity, PerformanceManagementMetaDataService } from '../meta-data/performancemanagement-meta-data.service';
import { EmployeeReviewQuestionSetupLevel } from '../meta-data/constants/employee-review-question-setup-level.enum';
import { inject } from '@angular/core/testing';
import { ethosUserContext } from '../../../shared/custom-contexts/dw-current-ethos-user-context.service';
import { AppPermissions } from '../../../meta-data/app-permissions.enum';
import { WachterUserExt } from '../../../shared/models/wachter-user.model';

@DwDatasource({
    keyName: 'employeeReviewQuestionSetupList',
    display: 'Employee Review Questions Setup Data Source',
    itemName: PerformanceManagementMetaDataItemNames.EmployeeReviewQuestionSetup,
})
@Injectable()
export class EmployeeReviewQuestionSetupListDataSourceService extends DwOrmDataSource {

    constructor(
        @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) metaDataService: DwMetaDataService,
        private dwContextService: DwContextService,
        private dwSecurityUserService: DwSecurityUserService
    ) {
        super(dwOrmDataService, metaDataService, 'EmployeeReviewQuestionSetup');
    }

    resolvedContext: any;
    departmentLookup: DepartmentLookupInfo[];
    teamLookup: TeamLookupInfo[];

    requestData(request: DwDataRequest): Observable<DwDataPageResults> {

        // We need several pieces of data to dynamically build the list

        const contextOb = this.dwContextService.getContextsAndResolve([ethosUserContext, 'dateTimeContext'], {});
        const departmentLookupOb = this.metaDataService.getLookup(PerformanceManagementMetaDataLookups.EthosWachterDepartmentAllItems);
        const teamLookupOb = this.metaDataService.getLookup(PerformanceManagementMetaDataLookups.EthosWachterTeamAllItems);

        return combineLatest([contextOb, departmentLookupOb, teamLookupOb])
            .pipe(mergeMap(([resolvedContext, departmentLookup, teamLookup]) => {

                this.resolvedContext = resolvedContext;
                this.departmentLookup = departmentLookup;
                this.teamLookup = teamLookup;

                return super.requestData(request)
                    .pipe(map(data => {
                        const setupList = data.data as EmployeeReviewQuestionSetupEntity[];

                        return this.buildFinalList(data, resolvedContext, departmentLookup, teamLookup, request);
                    }));
            }));
    }

    prepareRequestQuery(request: DwDataRequest): DwQuery {

        const query = super.prepareRequestQuery(request);

        const md = new PerformanceManagementMetaDataService();

        if (!query.Prefetch || query.Prefetch.length == 0) {
            query.AddPrefetchPath(md.EmployeeReviewQuestionSetup.Relations.EmployeeReviewQuestionSetupDetail.RelationName);
        }

        this.addFiltersByPermission(query)

        return query;
    }


    buildFinalList(data: DwDataPageResults, resolvedContext: any, departmentLookup: DwLookupInfo[], teamLookup: DwLookupInfo[], request: DwDataRequest): DwDataPageResults {
        const ethosUser: EthosUserEntity = resolvedContext[ethosUserContext];

        const setupList = this.filterOriginalData(data.data as EmployeeReviewQuestionSetupEntity[], ethosUser);

        const filterDetail = this.getFilterDetail(request);
        const currentYear = this.getCurrentYear(resolvedContext);

        if (!ethosUser?.WachterDepartmentId) {
            return data;
        }

        // If we on the current year, then we need to add any levels that haven't been setup year (this adds the cards with the plus)
        if (!filterDetail.reviewYear || filterDetail.reviewYear ==  currentYear) {
            this.addMissingLevels(setupList, ethosUser, departmentLookup, teamLookup, filterDetail, currentYear);
        }

        // Sort the results into a more logical view
        data.data = this.sortResults(setupList);

        return data;
    }

        // let infoToAdd = this.determineDepartmentsTeamsToAdd(ethosUser, departmentLookup, teamLookup);

        // // Add Global
        // if (this.dwSecurityUserService.hasPermission(AppPermissions.performanceManagementQuestionAdmin)) {
        //     const existingItem = setupList.find(item => item.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.global && item.ReviewYear == currentYear);

        //     if (!existingItem && this.includeGlobal(filterDetail)) {
        //         const newItem: EmployeeReviewQuestionSetupEntity = this.createGlobal(currentYear);

        //         setupList.push(newItem);
        //     }
        // }

        // // Add departments
        // for (const department of infoToAdd.departmentsToAdd) {
        //     const existingItem = setupList.find(item => item.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.department && item.WachterDepartmentId?.toString() == department.Id && item.ReviewYear == currentYear);

        //     if (!existingItem && this.includeDepartment(filterDetail, department)) {
        //         const newItem: EmployeeReviewQuestionSetupEntity = this.createDepartment(department, currentYear);

        //         setupList.push(newItem);
        //     }
        // }

        // // add teams
        // for (const team of infoToAdd.teamsToAdd) {
        //     const existingItem = setupList.find(item => item.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.team && item.WachterTeamId?.toString() == team.Id && item.ReviewYear == currentYear);

        //     if (!existingItem && this.includeTeam(filterDetail, team)) {
        //         const newItem: EmployeeReviewQuestionSetupEntity = this.createTeam(team, currentYear);

        //         setupList.push(newItem);
        //     }
        // }

      

    private addMissingLevels(setupList: EmployeeReviewQuestionSetupEntity[], ethosUser: EthosUserEntity, departmentLookup: DwLookupInfo[], teamLookup: DwLookupInfo[], filterDetail : FilterDetail, currentYear: number) {
        let infoToAdd = this.determineDepartmentsTeamsToAdd(ethosUser, departmentLookup, teamLookup);

        // Add Global
        if (this.dwSecurityUserService.hasPermission(AppPermissions.performanceManagementQuestionAdmin)) {
            const existingItem = setupList.find(item => item.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.global && item.ReviewYear == currentYear);

            if (!existingItem && this.includeGlobal(filterDetail)) {
                const newItem: EmployeeReviewQuestionSetupEntity = this.createGlobal(currentYear);

                setupList.push(newItem);
            }
        }

        // Add departments
        for (const department of infoToAdd.departmentsToAdd) {
            const existingItem = setupList.find(item => item.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.department && item.WachterDepartmentId?.toString() == department.Id && item.ReviewYear == currentYear);

            if (!existingItem && this.includeDepartment(filterDetail, department)) {
                const newItem: EmployeeReviewQuestionSetupEntity = this.createDepartment(department, currentYear);

                setupList.push(newItem);
            }
        }

        // add teams
        for (const team of infoToAdd.teamsToAdd) {
            const existingItem = setupList.find(item => item.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.team && item.WachterTeamId?.toString() == team.Id && item.ReviewYear == currentYear);

            if (!existingItem && this.includeTeam(filterDetail, team)) {
                const newItem: EmployeeReviewQuestionSetupEntity = this.createTeam(team, currentYear);

                setupList.push(newItem);
            }
        }
    }

    filterOriginalData(data: EmployeeReviewQuestionSetupEntity[], ethosUser: EthosUserEntity): EmployeeReviewQuestionSetupEntity[] {
        let trimmedData: EmployeeReviewQuestionSetupEntity[] = [];

        if (this.dwSecurityUserService.hasPermission(AppPermissions.performanceManagementQuestionAdmin)) {

            return data;
            // const globalItems = data.filter(item => item.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.global);

            // trimmedData = [...globalItems];
        }

        if (this.dwSecurityUserService.hasPermission(AppPermissions.performanceManagementDivisionQuestionSetup)) {
            const assignedDepartments = this.getAssignedDepartmentByDivision(ethosUser);

            const departmentItems = data.filter(item => assignedDepartments.find(d => d.Id == item.WachterDepartmentId.toString()) && (item.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.department || item.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.team));

            trimmedData = [...trimmedData, ...departmentItems];

            return trimmedData;
        }

        const assignedDepartments = this.getAssignedDepartments(ethosUser);
        if (assignedDepartments.length > 0) {
            const departmentItems = data.filter(item => assignedDepartments.find(d => d.Id == item.WachterDepartmentId.toString()) && (item.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.department || item.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.team));

            trimmedData = [...trimmedData, ...departmentItems];

            return trimmedData;
        }


        const assignedTeams = this.getAssignedTeams(ethosUser);

        if (assignedTeams.length > 0) {
            const teamItems = data.filter(item => assignedTeams.find(d => d.Id == item.WachterTeamId.toString())  && item.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.team);

            trimmedData = [...trimmedData, ...teamItems];
        }

        return trimmedData;
    }

    private getCurrentYear(resolvedContext: any) {
        return resolvedContext['dateTimeContext']?.CurrentYear;
    }

    includeGlobal(filterDetail: FilterDetail): boolean {
        if (filterDetail.wachterDepartmentId || filterDetail.wachterTeamId) {
            return false;
        }

        if (!filterDetail.levels) {
            return true;
        }

        return !!filterDetail.levels?.find(level => level == EmployeeReviewQuestionSetupLevel.global);
    }

    includeDepartment(filterDetail: FilterDetail, department: DwLookupInfo): boolean {

        if (filterDetail.wachterTeamId) {
            return false;
        }

        if (filterDetail.wachterDepartmentId && filterDetail.wachterDepartmentId?.toString() != department.Id) {
            return false;
        }

        if (!filterDetail.levels) {
            return true;
        }

        return !!filterDetail.levels?.find(level => level == EmployeeReviewQuestionSetupLevel.department);
    }

    includeTeam(filterDetail: FilterDetail, department: DwLookupInfo): boolean {

        if (filterDetail.wachterTeamId && filterDetail.wachterTeamId?.toString() != department.Id) {
            return false;
        }

        if (filterDetail.wachterDepartmentId && filterDetail.wachterDepartmentId?.toString() != department.Id) {
            return false;
        }

        if (!filterDetail.levels) {
            return true;
        }

        return !!filterDetail.levels?.find(level => level == EmployeeReviewQuestionSetupLevel.team);
    }

    private createGlobal(reviewYear: number): EmployeeReviewQuestionSetupEntity {
        const setup: EmployeeReviewQuestionSetupEntity = {
            _itemName: PerformanceManagementMetaDataItemNames.EmployeeReviewQuestionSetup
        };

        setup.EmployeeReviewQuestionSetupLevelId = EmployeeReviewQuestionSetupLevel.global;
        setup.EmployeeReviewQuestionSetupLevelIdDisplay = 'Global';
        setup.ReviewYear = reviewYear;

        return setup;
    }

    private createDepartment(department: DwLookupInfo, reviewYear: number): EmployeeReviewQuestionSetupEntity {
        const setup: EmployeeReviewQuestionSetupEntity = {
            _itemName: PerformanceManagementMetaDataItemNames.EmployeeReviewQuestionSetup
        };


        setup.EmployeeReviewQuestionSetupLevelId = EmployeeReviewQuestionSetupLevel.department;
        setup.EmployeeReviewQuestionSetupLevelIdDisplay = 'Department';

        setup.WachterDepartmentId = parseInt(department.Id);
        setup.WachterDepartmentIdDisplay = department.Display;

        setup.ReviewYear = reviewYear;

        return setup;
    }

    private createTeam(team: DwLookupInfo, reviewYear: number): EmployeeReviewQuestionSetupEntity {
        const setup: EmployeeReviewQuestionSetupEntity = {
            _itemName: PerformanceManagementMetaDataItemNames.EmployeeReviewQuestionSetup
        };

        setup.EmployeeReviewQuestionSetupLevelId = EmployeeReviewQuestionSetupLevel.team;
        setup.EmployeeReviewQuestionSetupLevelIdDisplay = 'Team';

        setup.WachterTeamId = parseInt(team.Id);
        setup.WachterTeamIdDisplay = team.Display;
        setup.WachterDepartmentId = parseInt(team.WachterDepartmentId?.toString());

        setup.ReviewYear = reviewYear;

        return setup;
    }


    private determineDepartmentsTeamsToAdd(ethosUser: EthosUserEntity, departmentLookup: DwLookupInfo[], teamLookup: DwLookupInfo[]): DepartmentTeamInfo {
        let result: DepartmentTeamInfo = { departmentsToAdd: [], teamsToAdd: [] };

        // Question Admins can see everything
        if (this.dwSecurityUserService.hasPermission(AppPermissions.performanceManagementQuestionAdmin)) {
            result.departmentsToAdd = departmentLookup;
            result.teamsToAdd = teamLookup;

            return result;
        }

        // Division Admins can see all departments in their division
        if (this.dwSecurityUserService.hasPermission(AppPermissions.performanceManagementDivisionQuestionSetup)) {
            // Limit to departments in the division and those departments teams
            result.departmentsToAdd = this.getAssignedDepartmentByDivision(ethosUser);
            result.teamsToAdd = teamLookup.filter(t => result.departmentsToAdd.find(d => d.Id == t.WachterDepartmentId?.toString()));

            if (result.departmentsToAdd.length==0) {
                return null;
            }

            return result;
        } 

        // Get all assigned departments
        const assignedDepartments = this.getAssignedDepartments(ethosUser);

        if (assignedDepartments.length > 0) {
           // Limit to the single department and their teams
            result.departmentsToAdd = assignedDepartments;
            result.teamsToAdd = teamLookup.filter(t => result.departmentsToAdd.find(d => d.Id == t.WachterDepartmentId?.toString()));

            return result;
        }

        // Get all assigned teams
        const assignedTeams = this.getAssignedTeams(ethosUser);

        if (assignedTeams.length > 0) {
            // Limit to the single department and their teams
            result.departmentsToAdd = []
            result.teamsToAdd = this.getAssignedTeams(ethosUser);

            return result;
        }

        return result;
    }

    /**
     * Sort results by level, then departments, then list the teams for the department
     * @param data 
     * @returns 
     */
    private sortResults(data: EmployeeReviewQuestionSetupEntity[]): EmployeeReviewQuestionSetupEntity[] {

        const sortedData: EmployeeReviewQuestionSetupEntity[] = [];

        // First add global
        const global = data.find(l => l.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.global);

        if (global) {
            sortedData.push(global);
        }

        const departments = sortArrayByField(data.filter(l => l.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.department), 'ReviewYear', 'WachterDepartmentIdDisplay');

        let existingRecords = true;

        // Add existing departments (and their teams) first, then new departments
        this.addDepartments(data, sortedData, departments, true);
        this.addDepartments(data, sortedData, departments, false);

        if (departments.length == 0) {
            const teams = sortArrayByField(data.filter(l => l.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.team), 'ReviewYear', 'WachterTeamIdDisplay');

            // Add existing teams first, then new teams
            this.addTeams(sortedData, teams, true);
            this.addTeams(sortedData, teams, false);
        }

        return sortedData;
    }

    private addDepartments(data: EmployeeReviewQuestionSetupEntity[], sortedData: EmployeeReviewQuestionSetupEntity[], departments: EmployeeReviewQuestionSetupEntity[], existingRecords: boolean) {
        for (const department of departments) {
            const isExisting = this.isExisting(department);
            if ((!existingRecords && isExisting) || (existingRecords && !isExisting)) {
                continue;
            }

            sortedData.push(department);

            const teams = sortArrayByField(data.filter(l => l.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.team && l.WachterDepartmentId == department.WachterDepartmentId && l.ReviewYear == department.ReviewYear), 'ReviewYear', 'WachterTeamIdDisplay');

            // Add existing teams first, then new teams
            this.addTeams(sortedData, teams, true);
            this.addTeams(sortedData, teams, false);
        }
    }

    private isExisting(item: EmployeeReviewQuestionSetupEntity): boolean {

        if (item.EmployeeReviewQuestionSetupId && item.EmployeeReviewQuestionSetupDetail?.length > 0) {
            return true;
        }

        return false;
    }

    private addTeams(sortedData: EmployeeReviewQuestionSetupEntity[], teams: EmployeeReviewQuestionSetupEntity[], existingRecords: boolean) {

        for (const team of teams) {

            const isExisting = this.isExisting(team);

            if ((!existingRecords && isExisting) || (existingRecords && !isExisting)) {
                continue;
            }

            sortedData.push(team);
        }
    }

    private getFilterDetail(request: DwDataRequest): FilterDetail {
        const filterDetail: FilterDetail = {};

        filterDetail.levels = request.filters?.Group1?.EmployeeReviewQuestionSetupLevelId?.In;
        filterDetail.reviewYear = request.filters?.ReviewYear?.Equal;
        filterDetail.wachterDepartmentId = request.filters?.WachterDepartmentId?.Equal;
        filterDetail.wachterTeamId = request.filters?.WachterTeamId?.Equal;

        return filterDetail;
    }


    addFiltersByPermission(query: DwQuery) {
        if (!this.resolvedContext) {
            return;
        }

        const ethosUser: EthosUserEntity = this.resolvedContext[ethosUserContext];

        if (!ethosUser) {
            return;
        }

        const qh = new DwQueryHelper();
        const md = new PerformanceManagementMetaDataService();

        // Question Admins can see everything
        if (this.dwSecurityUserService.hasPermission(AppPermissions.performanceManagementQuestionAdmin)) {
            // They can see everything
            return;
        }

        // Division Admins can see all departments in their division
        if (this.dwSecurityUserService.hasPermission(AppPermissions.performanceManagementDivisionQuestionSetup)) {
            const assignedDepartments = this.getAssignedDepartmentByDivision(ethosUser);


            const departmentIds = this.getLookupIds(assignedDepartments);

            query.AddFilterAnd(qh.filters.InList(md.EmployeeReviewQuestionSetup.WachterDepartmentId, departmentIds));

            return;
        }

        // Include any assigned departments
        const assignedDepartments = this.getAssignedDepartments(ethosUser);

        if (assignedDepartments.length>0) {
            const departmentIds = this.getLookupIds(assignedDepartments);

            query.AddFilterAnd(qh.filters.InList(md.EmployeeReviewQuestionSetup.WachterDepartmentId, departmentIds));

            return;
        }


        // Department Admins can see only their department
        const assignedTeams = this.getAssignedTeams(ethosUser);

        if (assignedTeams.length > 0) {
            const teamIds = this.getLookupIds(assignedTeams);

            query.AddFilterAnd(qh.filters.InList(md.EmployeeReviewQuestionSetup.WachterTeamId, teamIds));

            return;
        }
    }


    private getLookupIds(lookupItems: DwLookupInfo[]): number[] {
        return lookupItems.map(lookup => parseInt(lookup.Id));
    }

    private getAssignedDepartmentByDivision(ethosUser: EthosUserEntity): DepartmentLookupInfo[] {
        const assignedDepartments = this.getAssignedDepartments(ethosUser);

        let divisionId: number;
        if (assignedDepartments.length==0) {
            console.warn('Ethos user does not have any assigned departmentIds.  Using their ethos user department instead');
            divisionId = this.departmentLookup.find(d => d.Id == ethosUser.WachterDepartmentId.toString())?.WachterDivisionId;
        } else {
            divisionId = assignedDepartments[0].WachterDivisionId;
        }

        if (!divisionId) {
            return;
        }

        return this.departmentLookup.filter(d => d.WachterDivisionId == divisionId);
    }

    private getAssignedDepartments(ethosUser: EthosUserEntity): DepartmentLookupInfo[] {
        const trustUserId = ethosUser.UserId.toString();

        return this.departmentLookup.filter(dl => 
            dl.DepartmentAsstManagerUserId == trustUserId ||
            dl.DepartmentDirectorUserId == trustUserId ||
            dl.DepartmentManageerUserId == trustUserId
        );
    }

    private getAssignedTeams(ethosUser: EthosUserEntity): TeamLookupInfo[] {
        const trustUserId = ethosUser.UserId.toString();
        
        const lookupItems: TeamLookupInfo[] = [];

        return this.teamLookup.filter(tl => 
            tl.TeamLeaderUserId == trustUserId
        );
    }

}




export type DepartmentTeamInfo = { departmentsToAdd: DwLookupInfo[], teamsToAdd: DwLookupInfo[] };


export type FilterDetail = { levels?: number[], reviewYear?: number, wachterDepartmentId?: number, wachterTeamId?: number };

export interface DepartmentLookupInfo extends DwLookupInfo {
    WachterDivisionId?: number;
    DepartmentDirectorUserId?: string;
    DepartmentManageerUserId?: string;
    DepartmentAsstManagerUserId?: string;
}

export interface TeamLookupInfo extends DwLookupInfo {
    WachterDepartmentId?: number;
    TeamLeaderUserId?: string;
}